import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Layers, X, Lock, Radio, Type } from "lucide-react";
import api, { streamThumb } from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { SecureMedia } from "./Media";

export const CoverThumb = ({ cover }) => {
  if (cover && cover.stream_thumb)
    return <img src={streamThumb(cover.stream_thumb)} alt="" className="w-full h-full object-cover" />;
  if (cover && cover.text)
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#14A89A]/25 via-[#0A0A0A] to-[#0A0A0A] flex flex-col items-center justify-center p-3 text-center" data-testid="cover-text-note">
        <Type className="text-[#14A89A]/70 mb-1.5" size={18} />
        {cover.excerpt
          ? <span className="font-body text-[11px] text-zinc-300 leading-snug line-clamp-3">{cover.excerpt}</span>
          : <span className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500">Text note</span>}
      </div>
    );
  if (!cover || !cover.media || !cover.media.path)
    return <div className="w-full h-full bg-[#14A89A]/10 flex items-center justify-center"><Layers className="text-[#14A89A]/40" size={22} /></div>;
  return <SecureMedia path={cover.media.path} iv={cover.media.iv} enc={cover.enc} type={cover.media.type} className="w-full h-full object-cover" />;
};

// Fetch + decrypt titles for a member's collections.
export const useCollections = (ownerId) => {
  const { decryptNetText } = useCrypto();
  const [collections, setCollections] = useState(null);
  const reload = useCallback(() => {
    if (!ownerId) { setCollections([]); return; }
    api.get(`/collections?owner=${ownerId}`).then(async ({ data }) => {
      const out = [];
      for (const c of data) {
        let t = c.title || "Untitled collection";
        if (c.enc && c.title_ct) { const d = await decryptNetText(c.title_ct, c.title_iv); t = d === null ? "__LOCKED__" : (d || "Untitled collection"); }
        out.push({ ...c, _title: t });
      }
      setCollections(out);
    }).catch(() => setCollections([]));
    /* eslint-disable-next-line */
  }, [ownerId]);
  useEffect(() => { setCollections(null); reload(); }, [reload]);
  return { collections, reload };
};

export const CollectionCard = ({ c }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/collection/${c.id}`)} data-testid={`collection-card-${c.id}`} className="group text-left border border-white/10 hover:border-[#14A89A]/60 transition-colors overflow-hidden">
      <div className="aspect-square bg-black relative">
        <CoverThumb cover={c.cover} />
        <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full backdrop-blur-sm">{c.system === "stream" ? <Radio size={10} /> : <Layers size={10} />} {c.post_count}</div>
      </div>
      <div className="p-2.5">
        <div className="font-body text-sm text-white truncate group-hover:text-[#14A89A] transition-colors">{c._title === "__LOCKED__" ? <span className="text-zinc-400 flex items-center gap-1"><Lock size={11} /> Encrypted</span> : c._title}</div>
        <div className="font-body text-[10px] text-zinc-500 mt-0.5">{c.post_count} {c.post_count === 1 ? "piece" : "pieces"}</div>
      </div>
    </button>
  );
};

// Create-a-collection affordance. `primary` renders a filled CTA (used in empty states).
export const NewCollectionControl = ({ primary }) => {
  const navigate = useNavigate();
  const { encryptNetText, hasNetwork } = useCrypto();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      let payload = { title: title.trim(), enc: false };
      if (hasNetwork()) { const e = await encryptNetText(title.trim()); payload = { title_ct: e.ct, title_iv: e.iv, enc: true, title: "" }; }
      const { data } = await api.post("/collections", payload);
      navigate(`/collection/${data.id}`);
    } catch { alert("Could not create the collection."); setBusy(false); }
  };

  if (creating) {
    return (
      <div className="flex items-center gap-2 w-full max-w-md mx-auto" data-testid="new-collection-form">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Name your collection (e.g. 30-Day Garden Series)" data-testid="new-collection-title" autoFocus className="flex-1 bg-[#0A0A0A] border border-white/15 px-3 py-2.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] font-body text-sm" />
        <button onClick={create} disabled={busy || !title.trim()} data-testid="create-collection-button" className="bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-2.5 hover:bg-[#2BC4B5] transition-colors font-body text-sm flex items-center gap-1.5 disabled:opacity-40">{busy && <Loader2 size={13} className="animate-spin" />} Create</button>
        <button onClick={() => { setCreating(false); setTitle(""); }} className="text-zinc-500 hover:text-white p-2"><X size={16} /></button>
      </div>
    );
  }
  if (primary) {
    return <button onClick={() => setCreating(true)} data-testid="new-collection-button" className="inline-flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-2.5 hover:bg-[#2BC4B5] transition-colors font-body text-sm"><Plus size={15} /> Create your first collection</button>;
  }
  return <button onClick={() => setCreating(true)} data-testid="new-collection-button" className="inline-flex items-center gap-1.5 text-[#14A89A] hover:text-[#2BC4B5] font-body text-xs transition-colors"><Plus size={14} /> New collection</button>;
};
